import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Location } from 'history'
import AttendanceNoticeModal from './GlobalModals/AttendanceNoticeModal'

// A blink of a tab switch (an OS notification stealing focus, a file picker)
// should not trigger the reminder — only a real trip away from the hall does.
const AWAY_THRESHOLD_MS = 3000
// Exit intent fires on a guess, so it must not be able to nag: at most one
// reminder per this window, however many times the cursor crosses the top edge.
const EXIT_INTENT_COOLDOWN_MS = 60000

/**
 * Attendance reminder shown whenever the participant leaves the Plenary Hall,
 * because watch time is only recorded while this page is open AND visible
 * (see useTrack / useWatchPing).
 *
 * Ways out, each with its own handler:
 * - in-app navigation (Lobby button, nav links, browser back) -> history.block,
 *   our own modal, and the transition only runs after they click OK
 * - cursor heading for the tab strip / close button -> exit intent, our modal,
 *   shown BEFORE they click anything (a guess: mouse only, cannot block)
 * - closing the tab / typing another URL / reload -> beforeunload, which can
 *   only raise the browser's own native confirm dialog (the text is fixed by
 *   the browser, no page can customise it); if they cancel it, our modal
 *   follows
 * - switching to another tab or app -> nothing can be shown while the tab is
 *   hidden, so the reminder is raised on the way back
 *
 * Mounted by AuditoriumContainer, so it covers the desktop and mobile halls.
 */
export default function PlenaryLeaveGuard() {
  const history = useHistory()
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)

  const [pendingLocation, setPendingLocation] = useState<Location | null>(null)
  const [showAwayNotice, setShowAwayNotice] = useState(false)

  const unblockRef = useRef<(() => void) | null>(null)
  // Set once the participant has acknowledged: the transition that follows must
  // not be blocked (or prompted) a second time.
  const leavingRef = useRef(false)
  // Logout and the "logged in from another device" kick both navigate away on
  // their own; holding those transitions hostage would trap the user.
  const authedRef = useRef(isAuthenticated)
  const hiddenAtRef = useRef(0)
  const lastExitIntentRef = useRef(0)

  useEffect(() => {
    authedRef.current = isAuthenticated
  }, [isAuthenticated])

  useEffect(() => {
    const unblock = history.block((location) => {
      if (leavingRef.current || !authedRef.current) return undefined
      setPendingLocation(location)
      // `false` blocks the transition without going through the router's
      // window.confirm prompt, leaving the decision to our own modal.
      return false
    })
    unblockRef.current = unblock

    return () => {
      unblockRef.current = null
      unblock()
    }
  }, [history])

  useEffect(() => {
    let stayTimer: ReturnType<typeof setTimeout>

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (leavingRef.current) return
      event.preventDefault()
      // Chrome ignores preventDefault() alone; a non-undefined returnValue is
      // what actually raises the dialog. The wording ("Leave site?...") is the
      // browser's own and CANNOT be replaced with our message — a page has no
      // say in a beforeunload dialog. So the reminder is shown on the only path
      // we still control: if this timer ever fires, the page was not torn down,
      // which means they picked Cancel and are still in the hall.
      event.returnValue = ''
      stayTimer = setTimeout(() => setShowAwayNotice(true), 800)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      clearTimeout(stayTimer)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // Exit intent — the closest a page can get to "warn before they close the
  // tab". The cursor leaving through the TOP edge of the viewport is heading
  // for the tab strip, the close button or the address bar, so the reminder is
  // raised there, while the page is still alive and can render it. It is a
  // guess, not an event: it cannot block the close, it never fires on touch
  // devices, and leaving sideways or via cmd+W is invisible to it. The
  // beforeunload dialog below is what actually gates the close.
  useEffect(() => {
    const handleMouseOut = (event: MouseEvent) => {
      // relatedTarget is null only when the pointer left the document itself.
      if (event.relatedTarget || event.clientY > 0) return
      if (leavingRef.current) return

      const now = Date.now()
      if (now - lastExitIntentRef.current < EXIT_INTENT_COOLDOWN_MS) return
      lastExitIntentRef.current = now
      setShowAwayNotice(true)
    }

    document.addEventListener('mouseout', handleMouseOut)
    return () => document.removeEventListener('mouseout', handleMouseOut)
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now()
        return
      }

      const hiddenAt = hiddenAtRef.current
      hiddenAtRef.current = 0
      if (hiddenAt && Date.now() - hiddenAt >= AWAY_THRESHOLD_MS) {
        setShowAwayNotice(true)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const confirmLeave = useCallback(() => {
    const target = pendingLocation
    setPendingLocation(null)
    leavingRef.current = true
    if (unblockRef.current) unblockRef.current()
    if (target) history.push(target)
  }, [history, pendingLocation])

  const stay = () => {
    setPendingLocation(null)
    // They may have tabbed away while this modal was open; one reminder is
    // enough, don't queue a second one behind it.
    setShowAwayNotice(false)
  }

  if (pendingLocation) {
    return (
      <AttendanceNoticeModal
        showModal
        variant="leave"
        confirmLabel="OK, Leave"
        onConfirm={confirmLeave}
        hideModal={stay}
      />
    )
  }

  if (showAwayNotice) {
    return <AttendanceNoticeModal showModal hideModal={() => setShowAwayNotice(false)} />
  }

  return null
}
